import React, { useEffect, useRef } from 'react';

const SphereAnimation = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas dimensions
    canvas.width = 400;
    canvas.height = 400;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const boundaryRadius = 150;

    class Particle {
      constructor(direction) {
        const angle = Math.random() * Math.PI * 2;
        const radius = Math.random() * boundaryRadius;
        this.x = centerX + Math.cos(angle) * radius;
        this.y = centerY + Math.sin(angle) * radius;

        this.direction = direction;

        // Varying base speeds
        const speedAngle = Math.random() * Math.PI * 2;
        const baseSpeed = 0.3 + Math.random() * 0.8;
        this.vx = Math.cos(speedAngle) * baseSpeed;
        this.vy = Math.sin(speedAngle) * baseSpeed;

        // Varying sizes
        this.size = 1.5 + Math.random() * 2;
        this.originalSize = this.size;

        this.maxSpeed = 1.5 + (3.5 - this.size) * 0.4;
        this.personalSpace = 20 + this.size * 5;
        this.circularForce = 0.05 + Math.random() * 0.1;
      }

      update(particles) {
        let ax = 0;
        let ay = 0;

        // Particle avoidance
        particles.forEach(other => {
          if (other === this) return;

          const dx = other.x - this.x;
          const dy = other.y - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < this.personalSpace) {
            const force = (this.personalSpace - distance) / this.personalSpace;
            ax -= (dx / distance) * force * 0.05;
            ay -= (dy / distance) * force * 0.05;
            this.size = this.originalSize * (1 + force * 0.5);
          }
        });

        // Circular motion
        if (this.direction !== 0) {
          const dx = this.x - centerX;
          const dy = this.y - centerY;

          // Direction determines clockwise or counterclockwise
          ax += -dy * this.circularForce * this.direction;
          ay += dx * this.circularForce * this.direction;
        }

        // Boundary containment
        const dx = this.x - centerX;
        const dy = this.y - centerY;
        const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

        if (distanceFromCenter > boundaryRadius - this.size) {
          const force = (distanceFromCenter - (boundaryRadius - this.size)) * 0.1;
          ax -= (dx / distanceFromCenter) * force;
          ay -= (dy / distanceFromCenter) * force;
          this.vx *= 0.9;
          this.vy *= 0.9;
        }

        // Update velocity
        this.vx += ax;
        this.vy += ay;

        // Speed limit based on particle size
        const speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
        if (speed > this.maxSpeed) {
          this.vx = (this.vx / speed) * this.maxSpeed;
          this.vy = (this.vy / speed) * this.maxSpeed;
        }

        // Random movement
        this.vx += (Math.random() - 0.5) * 0.08;
        this.vy += (Math.random() - 0.5) * 0.08;

        // Update position
        this.x += this.vx;
        this.y += this.vy;

        // Return to original size
        this.size = this.size * 0.95 + this.originalSize * 0.05;
      }

      draw(ctx) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        let opacity = 0.7;
        if (this.direction === 1) opacity = 0.9;
        if (this.direction === -1) opacity = 0.8;
        ctx.fillStyle = `rgba(20, 184, 166, ${opacity})`;
        ctx.fill();
      }
    }

    // Create particles: 1/6 clockwise, 1/6 counterclockwise, 2/3 random
    const particles = [
      ...Array.from({ length: 30 }, () => new Particle(1)),   // Clockwise
      ...Array.from({ length: 30 }, () => new Particle(-1)),  // Counterclockwise
      ...Array.from({ length: 120 }, () => new Particle(0))   // Random
    ];

    let animationFrameId;

    function animate() {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      particles.forEach(particle => {
        particle.update(particles);
        particle.draw(ctx);
      });

      animationFrameId = requestAnimationFrame(animate);
    }

    animate();

    // Cleanup function to cancel animation frame when component unmounts
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []); // Empty dependency array means this effect runs once on mount

  return (
    <div className="w-full flex justify-center">
      <canvas
        ref={canvasRef}
        className="block bg-white"
      />
    </div>
  );
};

export default SphereAnimation;